import { SITE_ORIGIN } from "../siteConfig";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowRight, ClipboardList, Ruler, SlidersHorizontal, Sparkles } from "lucide-react";

import { EMPTY_FILTERS, buildFilterGroups, matchesFilters, results } from "../data/resultsData";
import useDocumentMeta from "../hooks/useDocumentMeta";
import ResultCard, { getMetaTags } from "../components/results/ResultCard";
import ResultsFilters, { FilterDrawer } from "../components/results/ResultsFilters";
import ResultModal from "../components/results/ResultModal";

gsap.registerPlugin(ScrollTrigger);

/* ─── Config ─────────────────────────────────────────────────────────────────
   CONSULT_HREF is the same appointment/contact link already used on your About page.
   HERO_TOP clears the fixed navbar; reduce it if your layout already adds top padding. */
const CONSULT_HREF = "/khargharNaviMumbai.php";
const HERO_TOP = "pt-28 md:pt-44";

const cinzel = "font-['Cinzel',serif]";
const inner = "relative z-[1] mx-auto max-w-[1200px] px-[18px] sm:px-6 lg:px-12";
const btnGold = `${cinzel} inline-flex items-center justify-center gap-2 rounded-full bg-[linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a)] bg-[length:200%] bg-left px-8 py-3.5 text-[11px] font-bold uppercase tracking-[0.16em] text-[#1a0f02] transition-all duration-300 hover:-translate-y-0.5 hover:bg-right hover:shadow-[0_8px_24px_rgba(201,168,76,0.35)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ffe9a0] motion-reduce:transition-none`;
const btnOutline = `${cinzel} inline-flex items-center justify-center gap-2 rounded-full border border-[#c9a84c]/60 px-8 py-3.5 text-[11px] font-bold uppercase tracking-[0.16em] text-[#ffe9a0] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#ffe9a0] hover:bg-[#c9a84c]/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ffe9a0] motion-reduce:transition-none`;

const highlights = [
  { icon: Ruler, title: "Personalized hairline design", text: "Every hairline is planned around your face and your goals." },
  { icon: Sparkles, title: "Natural-looking results", text: "Careful planning aimed at a natural, age-appropriate finish." },
  { icon: ClipboardList, title: "Individualized treatment planning", text: "A plan shaped around your hair, your donor area and your needs." },
];

function Eyebrow({ children, center = false, dark = false }) {
  const line = dark ? "bg-[#c9a84c]/50" : "bg-[#c9a84c]";
  return (
    <p
      className={`${cinzel} mb-3 flex items-center gap-3 text-[10.5px] uppercase tracking-[0.28em] ${
        dark ? "text-[#c9a84c]" : "text-[#a07828]"
      } ${center ? "justify-center" : ""}`}
    >
      <span className={`block h-px w-6 shrink-0 ${line}`} />
      {children}
      {center && <span className={`block h-px w-6 shrink-0 ${line}`} />}
    </p>
  );
}

export default function Results() {
  const rootRef = useRef(null);
  const galleryRef = useRef(null);
  const gridRef = useRef(null);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const groups = useMemo(() => buildFilterGroups(results), []);
  const [activeCat, setActiveCat] = useState(groups[0]?.key);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openId, setOpenId] = useState(null);

  useDocumentMeta({
    title: "Hair Transplant Before & After Results | HairClinique",
    description:
      "Explore real hair transplant before and after transformations. Filter results by graft range and technique, and compare each patient with an interactive slider.",
    canonical: typeof window !== "undefined" ? `${SITE_ORIGIN}/results` : undefined,
  });

  /* ── filtering ── */
  const filtered = useMemo(() => results.filter((r) => matchesFilters(r, filters)), [filters]);
  const activeCount = useMemo(() => Object.values(filters).reduce((n, arr) => n + arr.length, 0), [filters]);
  const filteredKey = filtered.map((r) => r.id).join(",");

  const toggle = useCallback((key, value) => {
    setFilters((f) => ({ ...f, [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value] }));
  }, []);
  const clearGroup = useCallback((key) => setFilters((f) => ({ ...f, [key]: [] })), []);
  const clearAll = useCallback(() => setFilters(EMPTY_FILTERS), []);

  /* ── modal ── */
  const openIndex = openId == null ? -1 : filtered.findIndex((r) => r.id === openId);
  const openResult = openIndex >= 0 ? filtered[openIndex] : null;
  const closeModal = useCallback(() => setOpenId(null), []);

  /* ── hero + scroll reveals (one context → one revert on unmount) ── */
  useLayoutEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    const ctx = gsap.context(() => {
      const ease = "power3.out";
      gsap
        .timeline({ defaults: { ease } })
        .fromTo("[data-hero]", { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.85, stagger: 0.12 })
        .fromTo("[data-hero-visual]", { opacity: 0, y: 36, scale: 0.97 }, { opacity: 1, y: 0, scale: 1, duration: 1 }, 0.25);

      // hide first, THEN create the triggers (already-visible items fire immediately)
      gsap.set("[data-reveal]", { opacity: 0, y: 28 });
      ScrollTrigger.batch("[data-reveal]", {
        start: "top 88%",
        once: true,
        onEnter: (batch) => gsap.to(batch, { opacity: 1, y: 0, duration: 0.75, ease, stagger: 0.1, overwrite: true }),
      });
    }, rootRef);

    return () => ctx.revert();
  }, []);

  /* ── re-reveal cards whenever the filtered set changes ── */
  useLayoutEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || !gridRef.current) return;
    const cards = gridRef.current.querySelectorAll("[data-card]");
    if (!cards.length) return;

    const ctx = gsap.context(() => {
      gsap.set(cards, { opacity: 0, y: 26 });
      ScrollTrigger.batch(cards, {
        start: "top 94%",
        once: true,
        onEnter: (batch) => gsap.to(batch, { opacity: 1, y: 0, duration: 0.6, ease: "power3.out", stagger: 0.07, overwrite: true }),
      });
    }, gridRef);

    return () => ctx.revert();
  }, [filteredKey]);

  const scrollToGallery = () => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    galleryRef.current?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
  };

  const featured = results[0];
  const featuredTags = featured ? getMetaTags(featured) : [];
  const catGroup = groups.find((g) => g.key === activeCat) || groups[0];

  return (
    <main ref={rootRef} className="relative w-full overflow-x-clip bg-[#f2ece0] font-['Raleway',sans-serif]">
      <link
        href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Raleway:wght@300;400;500;600&display=swap"
        rel="stylesheet"
      />

      {/* ═══════════ A. HERO ═══════════ */}
      <section
        className={`relative overflow-hidden pb-16 md:pb-24 ${HERO_TOP}`}
        style={{
          background:
            "radial-gradient(ellipse at 85% 10%, rgba(201,168,76,0.2), transparent 55%), radial-gradient(ellipse at 0% 100%, rgba(201,168,76,0.1), transparent 50%), linear-gradient(160deg, #1a0f02 0%, #0e0905 75%)",
        }}
      >
        <div className={inner}>
          <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2 md:gap-14">
            <div>
              <div data-hero>
                <Eyebrow dark>Real Patients. Real Results.</Eyebrow>
              </div>
              <h1
                data-hero
                className={`${cinzel} mb-5 text-[clamp(1.8rem,4vw,3rem)] font-black uppercase leading-[1.1] tracking-[0.04em] text-white`}
              >
                Hair Transplant
                <span className="block bg-[linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a)] bg-clip-text text-transparent">Before &amp; After Results</span>
              </h1>
              <p data-hero className="mb-8 max-w-[500px] text-[15px] leading-[1.85] text-white/75">
                Explore real hair restoration transformations. Browse by graft range and technique, then open any patient to compare their before and after side by side.
              </p>
              <div data-hero className="flex flex-col gap-3 sm:flex-row">
                <button type="button" onClick={scrollToGallery} className={btnGold}>
                  Explore Results
                  <ArrowRight size={15} aria-hidden="true" />
                </button>
                <a href={CONSULT_HREF} className={btnOutline}>
                  Book a Consultation
                </a>
              </div>
            </div>

            {/* featured real result — uses the first patient from the data file */}
            {featured && (
              <figure data-hero-visual className="relative mx-auto w-full max-w-[520px]">
                <div className="relative grid grid-cols-2 gap-px overflow-hidden rounded-[18px] border border-[#c9a84c]/40 bg-[#c9a84c]/40 shadow-[0_24px_70px_rgba(0,0,0,0.5)]">
                  <div className="relative aspect-[3/4] bg-[#0e0905]">
                    <img src={featured.before} alt={`Before hair transplant — ${featured.label}`} decoding="async" className="h-full w-full object-cover object-top" />
                    <span className={`${cinzel} absolute bottom-3 left-3 rounded-full bg-black/70 px-3 py-1 text-[9px] tracking-[0.16em] text-white/85`}>BEFORE</span>
                  </div>
                  <div className="relative aspect-[3/4] bg-[#0e0905]">
                    <img src={featured.after} alt={`After hair transplant — ${featured.label}`} decoding="async" className="h-full w-full object-cover object-top" />
                    <span className={`${cinzel} absolute bottom-3 right-3 rounded-full bg-[#c9a84c]/90 px-3 py-1 text-[9px] font-bold tracking-[0.16em] text-[#1a0f02]`}>AFTER</span>
                  </div>
                </div>
                <span aria-hidden="true" className="pointer-events-none absolute -left-2 -top-2 h-6 w-6 border-l-2 border-t-2 border-[#c9a84c]" />
                <span aria-hidden="true" className="pointer-events-none absolute -bottom-2 -right-2 h-6 w-6 border-b-2 border-r-2 border-[#c9a84c]" />
                <figcaption className="mt-3 text-center text-[12px] text-white/60">
                  {featured.label}
                  {featuredTags.length > 0 && ` · ${featuredTags.join(" · ")}`}
                </figcaption>
              </figure>
            )}
          </div>
        </div>
      </section>

      {/* ═══════════ B. HIGHLIGHTS ═══════════ */}
      <section aria-label="Why patients choose us" className="border-y border-[#c9a84c]/25 bg-white">
        <div className={`${inner} py-8 md:py-10`}>
          <ul data-reveal className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-0 md:divide-x md:divide-[#c9a84c]/20">
            {highlights.map(({ icon: Icon, title, text }) => (
              <li key={title} className="flex items-start gap-4 md:px-8 md:first:pl-0 md:last:pr-0">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#c9a84c]/30 bg-[linear-gradient(145deg,rgba(201,168,76,0.12),rgba(201,168,76,0.03))] text-[#a07828]">
                  <Icon size={20} strokeWidth={1.6} aria-hidden="true" />
                </span>
                <div>
                  <h2 className={`${cinzel} mb-1 text-[13px] font-bold uppercase tracking-[0.08em] text-[#1a0f02]`}>{title}</h2>
                  <p className="text-[13px] leading-[1.7] text-[#6b5535]">{text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ═══════════ C. BROWSE BY CATEGORY ═══════════ */}
      {catGroup && (
        <section className="pb-4 pt-14 md:pt-20">
          <div className={inner}>
            <div data-reveal className="mb-8 text-center">
              <Eyebrow center>Browse Results</Eyebrow>
              <h2 className={`${cinzel} text-[clamp(1.3rem,2.2vw,1.9rem)] font-black uppercase tracking-[0.06em] text-[#1a0f02]`}>
                Find a Transformation Like Yours
              </h2>
            </div>

            <div data-reveal className="mx-auto max-w-[860px]">
              {/* category tabs */}
              <div role="tablist" aria-label="Result categories" className="mb-5 flex flex-wrap justify-center gap-2">
                {groups.map((g) => {
                  const selected = g.key === catGroup.key;
                  return (
                    <button
                      key={g.key}
                      role="tab"
                      type="button"
                      aria-selected={selected}
                      onClick={() => setActiveCat(g.key)}
                      className={`${cinzel} rounded-full border px-5 py-2.5 text-[10.5px] font-bold uppercase tracking-[0.12em] transition-all duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#c9a84c] motion-reduce:transition-none ${
                        selected
                          ? "border-[#1a0f02] bg-[#1a0f02] text-[#ffe9a0]"
                          : "border-[#c9a84c]/40 bg-white text-[#6b5535] hover:border-[#c9a84c] hover:text-[#1a0f02]"
                      }`}
                    >
                      {g.tabLabel}
                    </button>
                  );
                })}
              </div>

              {/* chips for the selected category — share state with the filter panel */}
              <div role="group" aria-label={catGroup.label} className="flex flex-wrap justify-center gap-2">
                <button
                  type="button"
                  aria-pressed={(filters[catGroup.key] || []).length === 0}
                  onClick={() => clearGroup(catGroup.key)}
                  className={`rounded-full border px-4 py-2 text-[12.5px] font-medium transition-all duration-200 motion-reduce:transition-none ${
                    (filters[catGroup.key] || []).length === 0
                      ? "border-transparent bg-[linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a)] text-[#1a0f02]"
                      : "border-[#c9a84c]/30 bg-white text-[#6b5535] hover:border-[#c9a84c]"
                  }`}
                >
                  All
                </button>
                {catGroup.options.map((opt) => {
                  const on = (filters[catGroup.key] || []).includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggle(catGroup.key, opt.value)}
                      className={`rounded-full border px-4 py-2 text-[12.5px] font-medium transition-all duration-200 motion-reduce:transition-none ${
                        on
                          ? "border-transparent bg-[linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a)] text-[#1a0f02] shadow-[0_4px_14px_rgba(201,168,76,0.3)]"
                          : "border-[#c9a84c]/30 bg-white text-[#6b5535] hover:border-[#c9a84c] hover:text-[#1a0f02]"
                      }`}
                    >
                      {opt.label} <span className="opacity-60">({opt.count})</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ═══════════ D + E. FILTERS + GALLERY ═══════════ */}
      <section ref={galleryRef} id="results-gallery" className="scroll-mt-28 pb-20 pt-12 md:scroll-mt-40 md:pb-28 md:pt-16">
        <div className={inner}>
          <div data-reveal className="mb-8 text-center md:mb-10">
            <Eyebrow center>The Gallery</Eyebrow>
            <h2 className={`${cinzel} text-[clamp(1.3rem,2.2vw,1.9rem)] font-black uppercase tracking-[0.06em] text-[#1a0f02]`}>
              Patient Transformations
            </h2>
          </div>

          {/* toolbar: result count + mobile filter button */}
          <div className="mb-5 flex items-center justify-between gap-3">
            <p aria-live="polite" className="text-[13px] text-[#6b5535]">
              Showing <strong className="text-[#1a0f02]">{filtered.length}</strong> of {results.length} results
            </p>
            <div className="flex items-center gap-3">
              {activeCount > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="hidden text-[12px] font-medium text-[#a07828] underline decoration-[#c9a84c]/50 underline-offset-2 hover:text-[#1a0f02] sm:inline lg:hidden"
                >
                  Clear All Filters
                </button>
              )}
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className={`${cinzel} inline-flex items-center gap-2 rounded-full border border-[#c9a84c]/50 bg-white px-4 py-2.5 text-[10.5px] font-bold uppercase tracking-[0.12em] text-[#1a0f02] shadow-[0_2px_8px_rgba(90,60,10,0.08)] lg:hidden`}
              >
                <SlidersHorizontal size={14} aria-hidden="true" />
                Filters
                {activeCount > 0 && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#1a0f02] px-1 text-[10px] text-[#ffe9a0]">{activeCount}</span>
                )}
              </button>
            </div>
          </div>

          <div className="lg:grid lg:grid-cols-[290px_minmax(0,1fr)] lg:items-start lg:gap-8">
            {/* desktop sidebar */}
            <div className="mb-8 hidden lg:sticky lg:top-40 lg:mb-0 lg:block">
              <ResultsFilters
                groups={groups}
                filters={filters}
                onToggle={toggle}
                onClearGroup={clearGroup}
                onClearAll={clearAll}
                activeCount={activeCount}
                count={filtered.length}
                total={results.length}
              />
            </div>

            {/* grid / empty state */}
            <div>
              {filtered.length > 0 ? (
                <div ref={gridRef} className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                  {filtered.map((r) => (
                    <ResultCard key={r.id} result={r} onOpen={setOpenId} />
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-[#c9a84c]/50 bg-white/70 px-6 py-16 text-center">
                  <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-[#c9a84c]/30 text-[#a07828]">
                    <SlidersHorizontal size={22} aria-hidden="true" />
                  </span>
                  <h3 className={`${cinzel} mb-2 text-[16px] font-bold tracking-[0.04em] text-[#1a0f02]`}>No results match these filters</h3>
                  <p className="mx-auto mb-6 max-w-[360px] text-[13.5px] leading-[1.7] text-[#6b5535]">
                    Try removing a filter or two to see more patient transformations.
                  </p>
                  <button type="button" onClick={clearAll} className={btnGold}>
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ G. CONSULTATION CTA ═══════════ */}
      <section
        className="relative overflow-hidden py-16 md:py-24"
        style={{
          background:
            "radial-gradient(ellipse at 50% 0%, rgba(201,168,76,0.22), transparent 60%), linear-gradient(160deg, #1a0f02 0%, #0e0905 80%)",
        }}
      >
        <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-[linear-gradient(90deg,transparent,rgba(201,168,76,0.6),transparent)]" />
        <div className={`${inner} text-center`}>
          <div data-reveal>
            <Eyebrow center dark>Begin Your Journey</Eyebrow>
            <h2 className={`${cinzel} mx-auto mb-4 max-w-[720px] text-[clamp(1.5rem,3.2vw,2.4rem)] font-black uppercase leading-[1.15] tracking-[0.04em] text-white`}>
              Your Hair Restoration Journey Starts Here
            </h2>
            <p className="mx-auto mb-8 max-w-[560px] text-[15px] leading-[1.85] text-white/75">
              Speak with our team about your hair goals and what a personalized treatment plan could look like for you.
            </p>
            <a href={CONSULT_HREF} className={btnGold}>
              Book a Consultation
            </a>
          </div>
        </div>
      </section>

      {/* mobile filter drawer + detail modal */}
      <FilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        groups={groups}
        filters={filters}
        onToggle={toggle}
        onClearGroup={clearGroup}
        onClearAll={clearAll}
        activeCount={activeCount}
        count={filtered.length}
      />

      {openResult && (
        <ResultModal
          result={openResult}
          position={openIndex + 1}
          total={filtered.length}
          onClose={closeModal}
          onPrev={() => setOpenId(filtered[Math.max(0, openIndex - 1)].id)}
          onNext={() => setOpenId(filtered[Math.min(filtered.length - 1, openIndex + 1)].id)}
        />
      )}
    </main>
  );
}